import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_VEHICLE_STATUS
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

/** Normalize a plate/reg into a stable key (uppercase, no spaces). */
export const normalizeVehicleKey = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s/g, '');

/** Map Appwrite document → vehicle status/remark shape. */
export const formatVehicleStatus = (doc) => ({
  id: doc.$id ?? doc.id,
  vehicleKey: doc.vehicleKey || '',
  plate: doc.plate || '',
  status: doc.status || '',
  remark: doc.remark || '',
  updatedByEmail: doc.updatedByEmail || '',
  updatedByName: doc.updatedByName || ''
});

const toPayload = (fields) => ({
  vehicleKey: normalizeVehicleKey(fields.vehicleKey || fields.plate),
  plate: normalizeVehicleKey(fields.plate || fields.vehicleKey),
  status: String(fields.status || '').trim(),
  remark: String(fields.remark || '').trim(),
  updatedByEmail: String(fields.updatedByEmail || '').trim().toLowerCase(),
  updatedByName: String(fields.updatedByName || '').trim()
});

export const getVehicleStatusErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

/** List all vehicle status/remark overrides (paginated). */
export const fetchAllVehicleStatuses = async () => {
  const all = [];
  let cursor = null;
  const limit = 100;

  for (let page = 0; page < 50; page += 1) {
    const queries = [Query.limit(limit), Query.orderAsc('vehicleKey')];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_VEHICLE_STATUS,
      queries
    );
    const docs = response.documents || [];
    all.push(...docs.map(formatVehicleStatus));
    if (docs.length < limit) break;
    cursor = docs[docs.length - 1].$id;
  }

  return all;
};

const findByKey = async (vehicleKey) => {
  const key = normalizeVehicleKey(vehicleKey);
  if (!key) return null;
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTION_ID_VEHICLE_STATUS,
    [Query.equal('vehicleKey', key), Query.limit(1)]
  );
  return response.documents?.[0] ? formatVehicleStatus(response.documents[0]) : null;
};

/**
 * Create or update the status/remark for a vehicle (keyed by plate/reg).
 * When both status and remark are empty, the existing document is deleted.
 */
export const upsertVehicleStatus = async (fields) => {
  const payload = toPayload(fields);
  if (!payload.vehicleKey) {
    throw new Error('vehicleKey (plate) is required.');
  }

  const existing = await findByKey(payload.vehicleKey);

  if (!payload.status && !payload.remark) {
    if (existing?.id) {
      await databases.deleteDocument(
        DATABASE_ID,
        COLLECTION_ID_VEHICLE_STATUS,
        existing.id
      );
    }
    return null;
  }

  if (existing?.id) {
    const updated = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_VEHICLE_STATUS,
      existing.id,
      payload
    );
    return formatVehicleStatus(updated);
  }

  const created = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_VEHICLE_STATUS,
    ID.unique(),
    payload
  );
  return formatVehicleStatus(created);
};
